import React, { useState, useRef, useEffect, useImperativeHandle, useCallback } from "react";
import Input from "../../elements/input";
import { SuperButton } from "../../elements";
import { S_LANG, S_MARKET, webStorage } from "../../utils/webStorage";
import api from "../../utils/api";
import { isBizNull } from "../../utils";
import { connect } from "react-redux";
import { selectUserState, staffInfoSelector } from "../../selectors/application";
import { getMarketCountry, isLoggedIn } from "../../services/siteService";
import { addressTypes } from "../../utils/lynkco-constants";
import InputRow from "../../elements/input-row";

const ShippingInfoForm = React.forwardRef((props, ref) => {
  const { fields, isDisabled, address, savable = true, onSaveSuccess, user, staffInfo } = props;
  const {
    firstNameLabel,
    firstNameMissingMessage,
    lastNameLabel,
    lastNameMissingMessage,
    streetLabel,
    streetMissingMessage,
    apartmentLabel,
    postalCodeLabel,
    postalCodeMissingMessage,
    postalCodeWarningMessage,
    cityLabel,
    cityMissingMessage,
    // addressMissingMessage,
    // addressUnsavedMessage,
    ctaName,
    ctaType,
    colorScheme,
    iconName
  } = fields;
  const lang = webStorage.get(S_LANG);
  const [firstName, setFirstName] = useState(address?.firstName);
  const [firstNameError, setFirstNameError] = useState(null);
  const [lastName, setLastName] = useState(address?.lastName);
  const [lastNameError, setLastNameError] = useState(null);
  const [street, setStreet] = useState(address?.street);
  const [streetError, setStreetError] = useState(null);
  const [apartment, setApartment] = useState(address?.apartment);
  const [apartmentError, setApartmentError] = useState(null);
  const [postalCode, setPostalCode] = useState(address?.postalCode);
  const [postalCodeError, setPostalCodeError] = useState(null);
  const [postalCodeIsValid, setPostalCodeIsValid] = useState(true);
  const [city, setCity] = useState(address?.city);
  const [cityError, setCityError] = useState(null);
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const isInStore = useCallback(() => !!staffInfo, [staffInfo]);
  const rootEl = useRef();
  useEffect(() => {
    setFirstName(address?.firstName);
    setLastName(address?.lastName);
    setStreet(address?.street);
    setApartment(address?.apartment);
    setPostalCode(address?.postalCode);
    setCity(address?.city);
  }, [address]);
  useImperativeHandle(ref, () => ({
    setError(error) {
      setFormError(error);
    },
    validateForm() {
      return {
        isValid: validateFields(),
        formData: {
          firstName,
          lastName,
          street,
          apartment,
          postalCode,
          city,
          unSave: true
        }
      };
    },
    getEl() {
      return rootEl.current;
    }
  }));
  const validateFields = () => {
    let valid = true;
    if (isBizNull(firstName)) {
      setFirstNameError(firstNameMissingMessage.value);
      valid = false;
    }
    if (isBizNull(lastName)) {
      setLastNameError(lastNameMissingMessage.value);
      valid = false;
    }
    if (isBizNull(street)) {
      setStreetError(streetMissingMessage.value);
      valid = false;
    }
    if (isBizNull(postalCode)) {
      setPostalCodeError(postalCodeMissingMessage.value);
      valid = false;
    } else if (!postalCodeIsValid) {
      setPostalCodeError(postalCodeWarningMessage.value);
      valid = false;
    }
    if (isBizNull(city)) {
      setCityError(cityMissingMessage.value);
      valid = false;
    }
    return valid;
  };

  const onAutocompleteLocation = address => {
    if (address.street) setStreet(address.street);
    setCity(address.city ? address.city : "");
    setPostalCode(address.postalCode ? address.postalCode : "");
  };

  const firstNameProps = {
    label: firstNameLabel.value,
    value: firstName,
    maxLength: 25,
    showError: firstNameError !== null,
    warningMessage: firstNameError,
    disabled: isDisabled,
    onFocus: evt => {
      setFirstNameError(null);
    },
    onBlur: evt => {
      setFirstName(evt.target.value);
    }
  };
  const lastNameProps = {
    label: lastNameLabel.value,
    value: lastName,
    maxLength: 25,
    showError: lastNameError !== null,
    warningMessage: lastNameError,
    disabled: isDisabled,
    onFocus: evt => {
      setLastNameError(null);
    },
    onBlur: evt => {
      setLastName(evt.target.value);
    }
  };

  const streetProps = {
    label: streetLabel.value,
    value: street,
    showError: streetError !== null,
    warningMessage: streetError,
    disabled: isDisabled,
    location: { onAutocomplete: onAutocompleteLocation },
    onFocus: evt => {
      setStreetError(null);
    },
    onBlur: evt => {
      setStreet(evt.target.value);
    }
  };
  const apartmentProps = {
    label: apartmentLabel.value,
    value: apartment,
    showError: apartmentError !== null,
    warningMessage: apartmentError,
    disabled: isDisabled,
    onFocus: evt => {
      setApartmentError(null);
    },
    onBlur: evt => {
      setApartment(evt.target.value);
    }
  };
  const postalCodeProps = {
    label: postalCodeLabel.value,
    value: postalCode,
    showError: postalCodeError !== null,
    warningMessage: postalCodeError,
    disabled: isDisabled,
    onFocus: evt => {
      setPostalCodeError(null);
    },
    onBlur: (evt, isValid) => {
      setPostalCode(evt.target.value);
      setPostalCodeIsValid(isValid);
    },
    className: "right-gap"
  };
  const cityProps = {
    label: cityLabel.value,
    value: city,
    showError: cityError !== null,
    warningMessage: cityError,
    disabled: isDisabled,
    onFocus: evt => {
      setCityError(null);
    },
    onBlur: evt => {
      setCity(evt.target.value);
    }
  };
  const btnProps = ctaName && {
    buttonText: ctaName.value,
    ctaType,
    colorScheme,
    iconName,
    isDisabled: submitting,
    callback: async () => {
      setFormError(null);
      if (validateFields()) {
        setSubmitting(true);
        const country = await getMarketCountry();
        // console.log("country", country);
        // console.log("address", address);
        let action = "create";
        const body = {
          firstName: user?.firstName,
          lastName: user?.lastName,
          city: city,
          street: street,
          apartment: apartment,
          postalCode: postalCode,
          countryCode: country.regionAbbr,
          countryId: country.regionId,
          country: country.regionName,
          defaultFlag: "0",
          addressType: addressTypes.shipping
        };
        if (address?.addressId) {
          action = "update";
          body.addressId = address.addressId;
          body.defaultFlag = address.defaultFlag;
        }
        await api.address[action](null, body);
        if (onSaveSuccess) {
          if (address?.addressId) {
            Object.assign(address, {
              ...body,
              firstName: firstName,
              lastName: lastName
            });
            onSaveSuccess(address);
          } else {
            onSaveSuccess({
              ...body,
              firstName: firstName,
              lastName: lastName
            });
          }
        }
      }
    }
  };
  return (
    <form className="shipping-info-form" ref={rootEl} name="shipping-info-form">
      <Input name="first-name" autocomplete="first-name" {...firstNameProps} />
      <Input name="family-name" autocomplete="family-name" {...lastNameProps} />
      <Input name="address-line1" autocomplete="address-line1" {...streetProps} />
      <Input name="address-line2" autocomplete="address-line2" {...apartmentProps} />
      <InputRow>
        <Input name="postal-code" autocomplete="postal-code" type={/^nl-nl$/i.test(lang) ? "postalCode" : null} {...postalCodeProps}/>
        <Input name="address-level2" autocomplete="address-level2" {...cityProps} />
      </InputRow>
      {formError && <div className="failed-message">{formError}</div>}
      {isLoggedIn() && savable && <SuperButton {...btnProps} />}
    </form>
  );
});
const mapStateToProps = state => ({
  user: selectUserState(state),
  staffInfo: staffInfoSelector(state)
});
ShippingInfoForm.displayName = "ShippingInfoForm";
export default connect(mapStateToProps, null, null, { forwardRef: true })(ShippingInfoForm);
